import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import SEO from '../components/SEO'
import ProductCard from '../components/ProductCard'
import CMSGallery from '../components/CMSGallery'
import { fetchCollectionV2 } from '../lib/cms'

export default function HomePage() {
  const [featuredProducts, setFeaturedProducts] = useState([])
  const [loading, setLoading] = useState(true)

  // Hero Webform state
  const [heroForm, setHeroForm] = useState({
    navn: '',
    telefon: '',
    email: '',
    besked: '',
    website_url: '',
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [submitError, setSubmitError] = useState(null)

  const handleFormChange = (e) => {
    const { name, value } = e.target
    setHeroForm((prev) => ({ ...prev, [name]: value }))
  }

  const handleFormSubmit = async (e) => {
    e.preventDefault()
    setIsSubmitting(true)
    setSubmitError(null)

    try {
      const res = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          website_url: heroForm.website_url,
          form_type: 'Hero Tilbudsforespørgsel',
          navn: heroForm.navn,
          telefon: heroForm.telefon,
          email: heroForm.email,
          beskrivelse: heroForm.besked,
          timestamp: new Date().toISOString(),
        }),
      })

      if (!res.ok) {
        throw new Error(`Serverfejl (${res.status})`)
      }

      setSubmitted(true)
    } catch (err) {
      console.error('Hero form submit error:', err)
      setSubmitError('Der skete en fejl under indsendelsen. Ring gerne direkte på 21 43 81 49.')
    } finally {
      setIsSubmitting(false)
    }
  }

  useEffect(() => {
    async function loadFeatured() {
      try {
        const data = await fetchCollectionV2('produkter', { limit: 4, sort: 'sortering', order: 'asc' })
        setFeaturedProducts(data.items || [])
      } catch (e) {
        console.error('Error fetching featured products:', e)
      } finally {
        setLoading(false)
      }
    }
    loadFeatured()
  }, [])

  return (
    <>
      <SEO
        title="Kryger Wet Blaster – Dansk produceret vandpoleringsmaskiner i høj kvalitet. Læs mere her"
        description="Dansk produceret vandpoleringsmaskiner i høj kvalitet. Effektiv og kemi-fri metalrestaurering til auto og industri."
      />

      {/* Full-width Hero Section with background image, text aligned to container */}
      <section className="relative w-full bg-slate-950 text-white overflow-hidden border-b border-slate-800">
        {/* Full-width background image with light opacity in dark */}
        <div className="absolute inset-0 z-0 overflow-hidden">
          <img
            src="https://assets.auxodev.dk/cdn-cgi/image/width=1920,quality=80,format=auto/assets/21ecc947-a8ab-45f2-a8fa-709ae6f8815d/2500px-9462.jpg"
            srcSet="
              https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=80,format=auto/assets/21ecc947-a8ab-45f2-a8fa-709ae6f8815d/2500px-9462.jpg 800w,
              https://assets.auxodev.dk/cdn-cgi/image/width=1400,quality=80,format=auto/assets/21ecc947-a8ab-45f2-a8fa-709ae6f8815d/2500px-9462.jpg 1400w,
              https://assets.auxodev.dk/cdn-cgi/image/width=2000,quality=80,format=auto/assets/21ecc947-a8ab-45f2-a8fa-709ae6f8815d/2500px-9462.jpg 2000w
            "
            sizes="100vw"
            alt="Krüger Wet Blaster værksted og vandpoleringsmaskiner"
            className="w-full h-full object-cover object-center opacity-25 animate-hero-zoom"
          />
        </div>
        
        {/* Content strictly aligned to the site's container line */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-20 lg:pt-28 lg:pb-32">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
            {/* Left side: Heading, text and CTA */}
            <div className="lg:col-span-7 space-y-6 text-left">
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-sky-950/80 border border-sky-500/40 text-sky-400 text-xs font-semibold uppercase tracking-wider rounded-none">
                <i className="fa-solid fa-industry text-[11px]" aria-hidden="true" />
                <span>Produceret på værkstedet i Danmark</span>
              </div>

              <h1 className="text-3xl min-[400px]:text-4xl sm:text-4xl lg:text-5xl font-extrabold text-white tracking-tight leading-[1.15] break-words hyphens-auto">
                Dansk produceret <span className="text-white block sm:inline">vandpoleringsmaskiner</span>
              </h1>

              <p className="text-base sm:text-lg text-slate-300 leading-relaxed max-w-2xl font-normal">
                Produceret på værkstedet i Brørup. Effektiv restaurering og vedligeholdelse af metal, plast og gummi – 100% kemi-frit til auto og industri.
              </p>

              <div className="pt-2 flex flex-wrap items-center gap-4">
                <Link
                  to="/produkter"
                  className="inline-flex items-center justify-center gap-2.5 bg-sky-600 hover:bg-sky-500 active:bg-sky-700 text-white font-semibold px-6 py-3.5 shadow-lg shadow-sky-600/25 transition-all text-sm group"
                >
                  <span>Udforsk maskiner & udstyr</span>
                  <i className="fa-solid fa-arrow-right text-xs transition-transform duration-200 group-hover:translate-x-1" aria-hidden="true" />
                </Link>

                <Link
                  to="/kontakt"
                  className="inline-flex items-center justify-center gap-2 bg-slate-800 hover:bg-slate-700 text-slate-100 font-medium px-6 py-3.5 border border-slate-700 transition-colors text-sm"
                >
                  <i className="fa-solid fa-phone text-sky-400 text-xs" aria-hidden="true" />
                  <span className="whitespace-nowrap">Kontakt os</span>
                </Link>
              </div>

              {/* Technical badges */}
              <div className="pt-8 border-t border-white grid grid-cols-1 sm:grid-cols-3 gap-6 text-left">
                <div>
                  <div className="text-base sm:text-lg font-semibold text-slate-100">0% Kemi</div>
                  <div className="text-xs text-slate-400 mt-0.5">100% miljøvenlig proces</div>
                </div>
                <div>
                  <div className="text-base sm:text-lg font-semibold text-slate-100">AISI 304</div>
                  <div className="text-xs text-slate-400 mt-0.5">Fuldsvejst rustfri kabine</div>
                </div>
                <div>
                  <div className="text-base sm:text-lg font-semibold text-slate-100">Værkstedet</div>
                  <div className="text-xs text-slate-400 mt-0.5">Produceret på værkstedet i DK</div>
                </div>
              </div>
            </div>

            {/* Right side: Hero Webform */}
            <div className="lg:col-span-5">
              <div className="bg-white border border-slate-200 rounded-none p-6 sm:p-7 shadow-2xl shadow-black/30 text-left">
                <div className="mb-5">
                  <h2 className="text-xl font-bold text-slate-900 tracking-tight">
                    Få et uforpligtende tilbud
                  </h2>
                  <p className="text-xs text-slate-600 mt-1 leading-relaxed">
                    Udfyld formularen – vi rådgiver dig gerne om den rette maskine eller løsning.
                  </p>
                </div>

                {submitted ? (
                  <div className="bg-slate-50 border border-slate-200 rounded-none p-6 text-center space-y-3">
                    <div className="text-sky-600 text-3xl mx-auto">
                      <i className="fa-solid fa-circle-check" aria-hidden="true" />
                    </div>
                    <h3 className="text-base font-bold text-slate-900">Mange tak for din besked</h3>
                    <p className="text-xs text-slate-600 leading-relaxed">
                      Vi har modtaget din henvendelse og vender hurtigst muligt tilbage til dig.
                    </p>
                    <button
                      type="button"
                      onClick={() => {
                        setSubmitted(false)
                        setHeroForm({ navn: '', telefon: '', email: '', besked: '', website_url: '' })
                      }}
                      className="text-xs text-sky-600 hover:text-sky-700 font-semibold underline pt-2 cursor-pointer"
                    >
                      Send en ny besked
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleFormSubmit} className="space-y-4">
                    {/* Antispam Honeypot */}
                    <input
                      type="text"
                      name="website_url"
                      value={heroForm.website_url}
                      onChange={handleFormChange}
                      tabIndex={-1}
                      autoComplete="off"
                      className="sr-only absolute"
                      aria-hidden="true"
                    />

                    <div>
                      <label htmlFor="hero-navn" className="block text-xs font-semibold text-slate-700 mb-1">
                        Navn <span className="text-sky-600">*</span>
                      </label>
                      <input
                        id="hero-navn"
                        type="text"
                        name="navn"
                        required
                        value={heroForm.navn}
                        onChange={handleFormChange}
                        placeholder="Dit fulde navn"
                        className="w-full bg-transparent border-0 border-b border-slate-300 rounded-none px-0 py-2.5 text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:border-sky-600 focus:ring-0 transition-colors"
                      />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="hero-telefon" className="block text-xs font-semibold text-slate-700 mb-1">
                          Telefon <span className="text-sky-600">*</span>
                        </label>
                        <input
                          id="hero-telefon"
                          type="tel"
                          name="telefon"
                          required
                          value={heroForm.telefon}
                          onChange={handleFormChange}
                          placeholder="F.eks. 21 43 81 49"
                          className="w-full bg-transparent border-0 border-b border-slate-300 rounded-none px-0 py-2.5 text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:border-sky-600 focus:ring-0 transition-colors"
                        />
                      </div>
                      <div>
                        <label htmlFor="hero-email" className="block text-xs font-semibold text-slate-700 mb-1">
                          Email <span className="text-sky-600">*</span>
                        </label>
                        <input
                          id="hero-email"
                          type="email"
                          name="email"
                          required
                          value={heroForm.email}
                          onChange={handleFormChange}
                          placeholder="din@email.dk"
                          className="w-full bg-transparent border-0 border-b border-slate-300 rounded-none px-0 py-2.5 text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:border-sky-600 focus:ring-0 transition-colors"
                        />
                      </div>
                    </div>

                    <div>
                      <label htmlFor="hero-besked" className="block text-xs font-semibold text-slate-700 mb-1">
                        Hvad kan vi hjælpe dig med? <span className="text-sky-600">*</span>
                      </label>
                      <textarea
                        id="hero-besked"
                        name="besked"
                        rows={3}
                        required
                        value={heroForm.besked}
                        onChange={handleFormChange}
                        placeholder="Fortæl kort om dit projekt, opgave eller hvilken maskine du kigger på..."
                        className="w-full bg-transparent border-0 border-b border-slate-300 rounded-none px-0 py-2.5 text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:border-sky-600 focus:ring-0 transition-colors resize-none"
                      />
                    </div>

                    {submitError && (
                      <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-none p-2.5">
                        {submitError}
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full bg-sky-600 hover:bg-sky-500 active:bg-sky-700 disabled:opacity-60 text-white font-semibold py-3 px-5 rounded-none shadow-md shadow-sky-600/20 transition-all text-sm flex items-center justify-center gap-2 cursor-pointer"
                    >
                      {isSubmitting ? (
                        <>
                          <i className="fa-solid fa-spinner fa-spin text-sm" aria-hidden="true" />
                          <span>Sender forespørgsel...</span>
                        </>
                      ) : (
                        <>
                          <span>Indsend forespørgsel</span>
                          <i className="fa-solid fa-paper-plane text-xs" aria-hidden="true" />
                        </>
                      )}
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Feature Highlight: Effektiv metalrestaurering */}
      <section className="py-14 sm:py-16 bg-white border-b border-slate-200/80">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center space-y-3 mb-12">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-slate-900 tracking-tight">
              Effektiv metalrestaurering med Krüger Wet Blaster
            </h2>
            <div className="w-16 h-1 bg-sky-500 mx-auto rounded" />
            <p className="text-slate-600 leading-relaxed text-base pt-1">
              Rengør støbejern, rustfrit stål, aluminium, titanium, messing og gummi skånsomt og effektivt uden kemikalier.
            </p>
          </div>

          {/* 3 Categories / Application Areas */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Card 1: Kabiner */}
            <div className="bg-white border border-slate-200/90 rounded-none p-6 shadow-md hover:shadow-lg transition-shadow flex flex-col justify-between">
              <div>
                <div className="aspect-[16/10] rounded-none overflow-hidden mb-5 bg-slate-200">
                  <img
                    src="https://assets.auxodev.dk/assets/21ecc947-a8ab-45f2-a8fa-709ae6f8815d/aero-8060-2.jpg"
                    alt="Kabiner"
                    loading="lazy"
                    className="w-full h-full object-cover"
                  />
                </div>
                <h3 className="text-lg font-bold uppercase tracking-wider text-slate-900 mb-2">
                  Kabiner
                </h3>
                <p className="text-sm text-slate-600 leading-relaxed">
                  Danskproducerede vandpoleringskabiner i fuldsvejst rustfrit stål (AISI 304). Fås i Auto-model (KWB-8060) og Industri-model (KWB-12080).
                </p>
              </div>
              <div className="pt-5 mt-4 border-t border-slate-200">
                <Link
                  to="/produkter?kategori=kabiner"
                  className="inline-flex items-center gap-2 text-sm font-semibold text-slate-900 hover:text-black"
                >
                  <span>Se kabiner</span>
                  <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
                </Link>
              </div>
            </div>

            {/* Card 2: Forbrugsmaterialer */}
            <div className="bg-white border border-slate-200/90 rounded-none p-6 shadow-md hover:shadow-lg transition-shadow flex flex-col justify-between">
              <div>
                <div className="aspect-[16/10] rounded-none overflow-hidden mb-5 bg-slate-200">
                  <img
                    src="https://assets.auxodev.dk/assets/21ecc947-a8ab-45f2-a8fa-709ae6f8815d/mask-group-55.jpg"
                    alt="Forbrugsmaterialer"
                    loading="lazy"
                    className="w-full h-full object-cover"
                  />
                </div>
                <h3 className="text-lg font-bold uppercase tracking-wider text-slate-900 mb-2">
                  Forbrugsmaterialer
                </h3>
                <p className="text-sm text-slate-600 leading-relaxed">
                  Blæsemedier, glasperler, korrosionsbeskyttelse og imprægnering til efterbehandling af overflader.
                </p>
              </div>
              <div className="pt-5 mt-4 border-t border-slate-200">
                <Link
                  to="/produkter?kategori=forbrugsmaterialer"
                  className="inline-flex items-center gap-2 text-sm font-semibold text-slate-900 hover:text-black"
                >
                  <span>Se forbrugsmaterialer</span>
                  <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
                </Link>
              </div>
            </div>

            {/* Card 3: Reservedele & Tilbehør */}
            <div className="bg-white border border-slate-200/90 rounded-none p-6 shadow-md hover:shadow-lg transition-shadow flex flex-col justify-between">
              <div>
                <div className="aspect-[16/10] rounded-none overflow-hidden mb-5 bg-slate-200">
                  <img
                    src="https://assets.auxodev.dk/assets/21ecc947-a8ab-45f2-a8fa-709ae6f8815d/print-9424-scaled.jpg"
                    alt="Reservedele og tilbehør"
                    loading="lazy"
                    className="w-full h-full object-cover"
                  />
                </div>
                <h3 className="text-lg font-bold uppercase tracking-wider text-slate-900 mb-2">
                  Reservedele & Tilbehør
                </h3>
                <p className="text-sm text-slate-600 leading-relaxed">
                  Originale reservedele, blæsepistoler, dyser og ekstraudstyr til optimering af kabiner og arbejdsgange.
                </p>
              </div>
              <div className="pt-5 mt-4 border-t border-slate-200">
                <Link
                  to="/produkter?kategori=reservedele-tilbehoer"
                  className="inline-flex items-center gap-2 text-sm font-semibold text-slate-900 hover:text-black"
                >
                  <span>Se reservedele</span>
                  <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Products from CMS */}
      <section className="py-16 sm:py-20 bg-slate-50 border-b border-slate-200/80">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between mb-10 gap-4">
            <div>
              <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">
                Populære produkter fra Krüger Wet Blaster
              </h2>
            </div>
            <Link
              to="/produkter"
              className="inline-flex items-center gap-2 text-sm font-semibold text-sky-600 hover:text-sky-700 self-start sm:self-auto"
            >
              <span>Se alle produkter</span>
              <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
            </Link>
          </div>

          {loading ? (
            <div className="py-12 text-center text-slate-400">
              <i className="fa-solid fa-spinner fa-spin text-2xl mb-2 text-sky-500" />
              <p className="text-sm">Henter produkter...</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {featuredProducts.map((p) => (
                <ProductCard key={p.id} product={p} />
              ))}
            </div>
          )}

          {/* Quick Category Link Row */}
          <div className="mt-12 p-6 rounded-xl bg-white border border-slate-200 shadow-sm flex flex-wrap items-center justify-between gap-4">
            <div>
              <h3 className="text-base font-bold text-slate-900">Udforsk produktkategorier</h3>
              <p className="text-xs text-slate-500 mt-0.5">Find præcis den maskine, kabine, blæser eller reservedel du søger</p>
            </div>
            <div className="flex flex-wrap items-center divide-x divide-slate-300">
              <Link
                to="/produkter?kategori=kabiner"
                className="px-3 py-1 text-xs font-semibold text-slate-700 hover:text-sky-600 transition-colors first:pl-0"
              >
                Kabiner
              </Link>
              <Link
                to="/produkter?kategori=vandpoleringsmaskiner"
                className="px-3 py-1 text-xs font-semibold text-slate-700 hover:text-sky-600 transition-colors"
              >
                Vandpoleringsmaskiner
              </Link>
              <Link
                to="/produkter?kategori=mobile-blaesere"
                className="px-3 py-1 text-xs font-semibold text-slate-700 hover:text-sky-600 transition-colors"
              >
                Mobile Blæsere
              </Link>
              <Link
                to="/produkter?kategori=kompressorer"
                className="px-3 py-1 text-xs font-semibold text-slate-700 hover:text-sky-600 transition-colors"
              >
                Kompressorer
              </Link>
              <Link
                to="/produkter?kategori=toerisblaesning"
                className="px-3 py-1 text-xs font-semibold text-slate-700 hover:text-sky-600 transition-colors"
              >
                Tørisblæsning
              </Link>
              <Link
                to="/produkter?kategori=forbrugsmaterialer"
                className="px-3 py-1 text-xs font-semibold text-slate-700 hover:text-sky-600 transition-colors"
              >
                Blæsemedier
              </Link>
              <Link
                to="/produkter?kategori=reservedele-tilbehoer"
                className="px-3 py-1 text-xs font-semibold text-slate-700 hover:text-sky-600 transition-colors"
              >
                Reservedele
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Funktionalitet & Teknisk Kvalitet - Strømlinet & Visuelt */}
      <section className="py-14 sm:py-16 bg-white border-b border-slate-200/80">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-6 space-y-5">
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-slate-900 tracking-tight">
                Giv dine metaldele nyt liv med Krüger Wet Blaster
              </h2>
              
              <p className="text-slate-600 text-base leading-relaxed">
                CE-godkendt og grundigt testet af Dansk Teknologisk Institut. Lukkede kabiner i kraftigt rustfrit stål (AISI 304) med integreret udsugning og ristebeskyttelse til skånsom behandling af alle typer metaller.
              </p>

              <div className="space-y-4 pt-2">
                <div>
                  <div className="text-sky-600 font-bold text-base mb-1">
                    CE & DTI
                  </div>
                  <p className="text-sm text-slate-600">Dokumenteret sikkerhed og funktionalitet efter europæiske standarder.</p>
                </div>
                <div>
                  <div className="text-sky-600 font-bold text-base mb-1">
                    Sundt arbejdsmiljø
                  </div>
                  <p className="text-sm text-slate-600">Lukket vådproces minimerer støv markant på værkstedet.</p>
                </div>
              </div>

              <div className="pt-2">
                <Link
                  to="/kontakt"
                  className="inline-flex items-center gap-2 bg-sky-600 hover:bg-sky-500 text-white font-semibold px-5 py-2.5 rounded-lg shadow-sm text-sm transition-colors"
                >
                  <i className="fa-solid fa-comments text-xs" aria-hidden="true" />
                  <span>Kontakt os for rådgivning</span>
                </Link>
              </div>
            </div>

            <div className="lg:col-span-6 space-y-4">
              <div className="rounded-none overflow-hidden border border-slate-200 shadow-md">
                <img
                  src="https://assets.auxodev.dk/assets/21ecc947-a8ab-45f2-a8fa-709ae6f8815d/2500px-9433.jpg"
                  alt="Krüger Wet Blaster kabine i værksted"
                  loading="lazy"
                  className="w-full h-auto object-cover max-h-[360px]"
                />
              </div>

              {/* Two column sub-images */}
              <div className="grid grid-cols-2 gap-4">
                <div className="rounded-none overflow-hidden border border-slate-200">
                  <img
                    src="https://assets.auxodev.dk/assets/21ecc947-a8ab-45f2-a8fa-709ae6f8815d/image6-225x300.jpeg"
                    alt="Før og efter restaurering"
                    loading="lazy"
                    className="w-full h-36 object-cover"
                  />
                </div>
                <div className="rounded-none overflow-hidden border border-slate-200">
                  <img
                    src="https://assets.auxodev.dk/assets/21ecc947-a8ab-45f2-a8fa-709ae6f8815d/image7-225x300.jpeg"
                    alt="Restaureret detalje"
                    loading="lazy"
                    className="w-full h-36 object-cover"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Two Key Benefits - integreret på denne sektion */}
          <div className="pt-12 mt-12 border-t border-slate-200/80 grid grid-cols-1 md:grid-cols-2 gap-10 lg:gap-16">
            {/* Benefit 1 */}
            <div className="space-y-2">
              <h3 className="text-lg sm:text-xl font-bold uppercase tracking-wide text-slate-900">
                Præcis, effektiv & høj kvalitet
              </h3>
              <p className="text-sm text-slate-600 leading-relaxed">
                Rens og restaurér metaldele med millimeterpræcision. Vores kontrollerede blande-konstruktion sikrer jævn vand- og mediebehandling for et fejlfrit resultat.
              </p>
            </div>

            {/* Benefit 2 */}
            <div className="space-y-2">
              <h3 className="text-lg sm:text-xl font-bold uppercase tracking-wide text-slate-900">
                Skånsom, miljøvenlig & 0% kemi
              </h3>
              <p className="text-sm text-slate-600 leading-relaxed">
                Beskyt medarbejderne, værkstedet og miljøet. Krüger Wet Blaster fjerner behovet for skadelige kemikalier og støvende tørblæsning.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Dynamic CMS Gallery (connected via technical slug 'maskiner-i-brug') */}
      <section className="py-16 sm:py-20 bg-slate-50 border-b border-slate-200/80">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-8">
            <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">
              Krüger Wet Blaster i praksis
            </h2>
            <p className="text-sm text-slate-600 mt-2">
              Se billeder fra vores produktion, værksteder og maskiner i funktion.
            </p>
          </div>

          <CMSGallery slug="maskiner-i-brug" limit={4} />
        </div>
      </section>

      {/* Customer Testimonials from Scrape */}
      <section className="py-16 sm:py-20 bg-white border-b border-slate-200/80">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">
              Hvad vores kunder siger
            </h2>
            <div className="w-12 h-1 bg-sky-500 mx-auto rounded mt-3" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Review 1: Vejles Autoværksted */}
            <blockquote className="bg-white rounded-xl p-7 border border-slate-200 shadow-sm flex flex-col justify-between">
              <div className="space-y-3">
                <div className="flex text-amber-400 gap-1 text-sm">
                  {[...Array(5)].map((_, i) => (
                    <i key={i} className="fa-solid fa-star" aria-hidden="true" />
                  ))}
                </div>
                <h3 className="font-bold text-base text-slate-900 tracking-tight">
                  Hurtigere proces & renere værksted
                </h3>
                <p className="text-sm text-slate-600 leading-relaxed italic">
                  &ldquo;Delene bliver så rene, at de ligner nye. En kæmpe gevinst har været, at vi næsten har fjernet alt støv på værkstedet – det kan mærkes i hverdagen.&rdquo;
                </p>
              </div>
              <footer className="pt-5 mt-5 border-t border-slate-200">
                <div className="font-bold text-slate-900 text-sm">Martin, Ejer</div>
                <div className="text-xs text-sky-600 font-semibold uppercase tracking-wide">Vejles Autoværksted</div>
              </footer>
            </blockquote>

            {/* Review 2: Strøjer Samlingen */}
            <blockquote className="bg-white rounded-xl p-7 border border-slate-200 shadow-sm flex flex-col justify-between">
              <div className="space-y-3">
                <div className="flex text-amber-400 gap-1 text-sm">
                  {[...Array(5)].map((_, i) => (
                    <i key={i} className="fa-solid fa-star" aria-hidden="true" />
                  ))}
                </div>
                <h3 className="font-bold text-base text-slate-900 tracking-tight">
                  Utrolig præcision & perfekt finish
                </h3>
                <p className="text-sm text-slate-600 leading-relaxed italic">
                  &ldquo;Vi har anvendt Krüger Wet Blaster til vores bilsamling, og resultaterne er fantastiske. Maskinen leverer en utrolig præcision og et perfekt finish.&rdquo;
                </p>
              </div>
              <footer className="pt-5 mt-5 border-t border-slate-200">
                <div className="font-bold text-slate-900 text-sm">Jørgen</div>
                <div className="text-xs text-sky-600 font-semibold uppercase tracking-wide">Strøjer Samlingen</div>
              </footer>
            </blockquote>

            {/* Review 3: Ejner Hessel */}
            <blockquote className="bg-white rounded-xl p-7 border border-slate-200 shadow-sm flex flex-col justify-between">
              <div className="space-y-3">
                <div className="flex text-amber-400 gap-1 text-sm">
                  {[...Array(5)].map((_, i) => (
                    <i key={i} className="fa-solid fa-star" aria-hidden="true" />
                  ))}
                </div>
                <h3 className="font-bold text-base text-slate-900 tracking-tight">
                  Høj kvalitet & lav miljøbelastning
                </h3>
                <p className="text-sm text-slate-600 leading-relaxed italic">
                  &ldquo;Afrensningen udføres hurtigt i en høj kvalitet og med lavest mulig miljøbelastning. Maskinen er så velbygget, at vi har installeret dem i stort antal.&rdquo;
                </p>
              </div>
              <footer className="pt-5 mt-5 border-t border-slate-200">
                <div className="font-bold text-slate-900 text-sm">Lars Hillker Madsen</div>
                <div className="text-xs text-sky-600 font-semibold uppercase tracking-wide">Facility Manager, Ejner Hessel</div>
              </footer>
            </blockquote>
          </div>
        </div>
      </section>

      {/* Partner & Reference Logos from Scrape */}
      <section className="py-14 bg-white border-b border-slate-200 overflow-hidden">
        {/* Endless Marquee / Carousel */}
        <div className="relative w-full overflow-hidden">
          {/* Gradient fade on left and right edges */}
          <div className="pointer-events-none absolute inset-y-0 left-0 w-16 sm:w-24 bg-gradient-to-r from-white to-transparent z-10" />
          <div className="pointer-events-none absolute inset-y-0 right-0 w-16 sm:w-24 bg-gradient-to-l from-white to-transparent z-10" />

          <div className="flex w-max animate-marquee gap-6 sm:gap-8 items-center py-2">
            {[
              {
                name: 'Ejner Hessel',
                src: 'https://assets.auxodev.dk/assets/21ecc947-a8ab-45f2-a8fa-709ae6f8815d/ejner.png',
                h: 'max-h-12',
              },
              {
                name: 'Reno',
                src: 'https://assets.auxodev.dk/assets/21ecc947-a8ab-45f2-a8fa-709ae6f8815d/reno-logo-rgb-black-text-scaled.png',
                h: 'max-h-10',
              },
              {
                name: 'Ibix',
                src: 'https://assets.auxodev.dk/assets/21ecc947-a8ab-45f2-a8fa-709ae6f8815d/ibix-logo-2.png',
                h: 'max-h-10',
              },
              {
                name: 'BSB',
                src: 'https://assets.auxodev.dk/assets/21ecc947-a8ab-45f2-a8fa-709ae6f8815d/bsb-logo.svg',
                h: 'max-h-10',
              },
              {
                name: 'Strøjer Samlingen',
                src: 'https://assets.auxodev.dk/assets/21ecc947-a8ab-45f2-a8fa-709ae6f8815d/strojerlogofritlagt.png',
                h: 'max-h-11',
              },
              {
                name: 'AU2PARTS',
                src: 'https://assets.auxodev.dk/assets/21ecc947-a8ab-45f2-a8fa-709ae6f8815d/au2parts-logo.svg',
                h: 'max-h-10',
              },
              {
                name: 'X-Yachts',
                src: 'https://assets.auxodev.dk/assets/21ecc947-a8ab-45f2-a8fa-709ae6f8815d/x-yachts-black.png',
                h: 'max-h-9',
              },
            ]
              .concat([
                {
                  name: 'Ejner Hessel',
                  src: 'https://assets.auxodev.dk/assets/21ecc947-a8ab-45f2-a8fa-709ae6f8815d/ejner.png',
                  h: 'max-h-12',
                },
                {
                  name: 'Reno',
                  src: 'https://assets.auxodev.dk/assets/21ecc947-a8ab-45f2-a8fa-709ae6f8815d/reno-logo-rgb-black-text-scaled.png',
                  h: 'max-h-10',
                },
                {
                  name: 'Ibix',
                  src: 'https://assets.auxodev.dk/assets/21ecc947-a8ab-45f2-a8fa-709ae6f8815d/ibix-logo-2.png',
                  h: 'max-h-10',
                },
                {
                  name: 'BSB',
                  src: 'https://assets.auxodev.dk/assets/21ecc947-a8ab-45f2-a8fa-709ae6f8815d/bsb-logo.svg',
                  h: 'max-h-10',
                },
                {
                  name: 'Strøjer Samlingen',
                  src: 'https://assets.auxodev.dk/assets/21ecc947-a8ab-45f2-a8fa-709ae6f8815d/strojerlogofritlagt.png',
                  h: 'max-h-11',
                },
                {
                  name: 'AU2PARTS',
                  src: 'https://assets.auxodev.dk/assets/21ecc947-a8ab-45f2-a8fa-709ae6f8815d/au2parts-logo.svg',
                  h: 'max-h-10',
                },
                {
                  name: 'X-Yachts',
                  src: 'https://assets.auxodev.dk/assets/21ecc947-a8ab-45f2-a8fa-709ae6f8815d/x-yachts-black.png',
                  h: 'max-h-9',
                },
              ])
              .map((partner, idx) => (
                <div
                  key={`${partner.name}-${idx}`}
                  className="p-4 bg-white rounded-none border border-slate-200/80 shadow-2xs w-48 sm:w-56 h-24 shrink-0 flex items-center justify-center hover:shadow-xs transition-shadow"
                >
                  <img
                    src={partner.src}
                    alt={partner.name}
                    className={`${partner.h} w-auto object-contain`}
                  />
                </div>
              ))}
          </div>
        </div>
      </section>

      {/* Bottom CTA Banner */}
      <section className="relative py-16 sm:py-20 bg-slate-950 text-white overflow-hidden">
        {/* Background image with overlay */}
        <div className="absolute inset-0 z-0">
          <img
            src="https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=80,format=auto/assets/21ecc947-a8ab-45f2-a8fa-709ae6f8815d/2500px-9458.jpg"
            alt="Krüger Wet Blaster produktion og maskiner"
            className="w-full h-full object-cover opacity-25"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/80 to-slate-950/90" />
        </div>

        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-6">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight">
            Klar til at optimere din afrensning med Krüger Wet Blaster?
          </h2>
          <p className="text-slate-300 text-sm sm:text-base max-w-2xl mx-auto">
            Vi kommer gerne ud til din virksomhed og ser på hvad vi kan gøre og hvad det er for afrensnings udfordringer i måtte stå overfor.
          </p>
          <div className="pt-2 flex flex-wrap items-center justify-center gap-4">
            <Link
              to="/bestil"
              className="inline-flex items-center gap-2 bg-sky-600 hover:bg-sky-500 text-white font-bold px-6 py-3.5 rounded-none shadow-md text-sm transition-all"
            >
              <i className="fa-solid fa-file-invoice" aria-hidden="true" />
              <span>Bestil produkt til faktura</span>
            </Link>
            <a
              href="tel:+4521438149"
              className="inline-flex items-center gap-2 bg-transparent hover:bg-transparent text-white font-semibold px-6 py-3.5 rounded-none border border-white text-sm transition-colors whitespace-nowrap"
            >
              <i className="fa-solid fa-phone" aria-hidden="true" />
              <span className="whitespace-nowrap">(+45) 21 43 81 49</span>
            </a>
          </div>
        </div>
      </section>
    </>
  )
}
